import React, { useMemo, useState, useEffect } from "react";

import SubjectiveCharts from "./SubjectiveCharts";
import { getDropdownKey } from "../../Utils/chart_utils";
import { useDispatch } from "react-redux";
import {
  Table,
  Space,
  Layout,
  Row,
  Col,
  Statistic,
  Switch,
  Select,
  Button,
} from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { FooterComponent } from "../Footer";
import {
  setWellnessStatDefaults,
  setTeamFilter,
} from "../../store/actions/team.actions.js";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { trainingAPI } from "../../api/TrainingAPI";
import { setPlayerFilter } from "../../store/actions/player.actions.js";
import { useSelector } from "react-redux";
import { getAnswerAndIndex, getValueColor } from "../../Utils/chart_utils";
import { isDateToday } from "../../Utils/date_utils";
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx"
import { StatTileV2 } from "../StatTileV2/StatTileV2.jsx"
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { get_metric_info_from_identifier } from "../../Utils/chart_utils";

const { Footer } = Layout;

export function Subjective({ type, ...props }) {
  const dispatch = useDispatch();
  const RPE = useMemo(
    () => props.data?.metrics?.find((item) => item.key === "RPE"),
    [props.data]
  );
  const {
    all_metric_types,
    wellnessStatDefaults,
		dropdowns: {wellness: wellnessDropdowns},
    misc: { comparison_types },
  } = useSelector(({ team }) => team);

  const {
    filter: { endDate, startDate, player, wellnessStats },
  } = useSelector((store) => store[type]);

  useMetricDropdowns(['wellness']);

  const [statMetrics, setStatMetrics] = useState([]);
  const [option, setOption] = useState("latest_vs_prev");

  useEffect(() => {
    setStatMetrics(wellnessStatDefaults);
  }, [wellnessStatDefaults]);

  const wellnessScore = useMemo(
    () =>
      props.data?.wellness_metrics?.find(
        (item) => item?.key === "wellness_score"
      ),
    [props.data]
  );

  const mergedData = useMemo(
    () =>
      props.data?.wellness
        ? Object.values(
            Object.values(props.data?.wellness)
              .flat()
              .reduce((merged, obj) => {
                const key = obj?.date || obj?.disp_date;
                merged[key] = merged[key] ? { ...merged[key], ...obj } : obj;
                return merged;
              }, {})
          )
        : [],
    [props.data]
  );

  const boxWellnessScore =
    useMemo(
      () => mergedData?.find((item) => isDateToday(item.date)),
      [mergedData]
    ) ?? 0;

  const avgWellnessScore = useMemo(() => {
    const arr = mergedData?.filter((item) => item.wellness_score);
    if (!arr.length) return "-";
    const total_wellness = arr.reduce(
      (sum, obj) => sum + obj?.wellness_score,
      0
    );
    const avg = total_wellness / arr.length;
    return +avg.toFixed(1);
  }, [mergedData]);

  const avgRPE = useMemo(() => {
    const DatesMatched = props?.data.data?.filter((item) =>
      isDateToday(item.date)
    );
    if (!DatesMatched.length) {
      return 0;
    }
    const totalRPE = DatesMatched?.reduce((sum, obj) => sum + obj?.RPE, 0);
    const averageRPE = totalRPE / DatesMatched?.length;
    return +averageRPE.toFixed(1);
  }, [props]);

  const todayDiffWel =
    (((boxWellnessScore?.wellness_score || 0) - avgWellnessScore) * 100.0) /
    avgWellnessScore;

  const boxes = [
    {
      title: props.playersPage
        ? "Today's wellness score"
        : "TODAYS TEAM AVERAGE",
      sub: boxWellnessScore?.wellness_score,
      className: boxWellnessScore?.wellness_score
        ? boxWellnessScore?.wellness_score >= wellnessScore?.upper
          ? "success"
          : boxWellnessScore?.wellness_score > wellnessScore?.lower
          ? "warning"
          : "danger"
        : "",
    },
    {
      title: props.playersPage
        ? "Historical wellness score"
        : "HISTORICAL TEAM AVERAGE",
      sub: `${avgWellnessScore > 0 ? avgWellnessScore?.toFixed(1) : ""} ${
        "(" +
        (todayDiffWel > 0 ? "+" : "-") +
        Math.round(Math.abs(todayDiffWel)) +
        "%)"
      }`,
      valueStyle:
        todayDiffWel > 0 ? { color: "#3f8600" } : { color: "#cf1322" },
      suffix:
        todayDiffWel > 0 ? (
          <ArrowUpOutlined style={{ position: "absolute", top: 34 }} />
        ) : (
          <ArrowDownOutlined style={{ position: "absolute", top: 34 }} />
        ),
      className: avgWellnessScore
        ? avgWellnessScore - 10 > boxWellnessScore?.wellness_score
          ? "danger"
          : avgWellnessScore + 10 < boxWellnessScore?.wellness_score
          ? "success"
          : ""
        : "",
    },
    {
      title: "TODAYS RPE AVERAGE",
      sub: avgRPE || "-",
      className: avgRPE
        ? avgRPE >= RPE?.upper
          ? "danger"
          : avgRPE > RPE?.lower
          ? "warning"
          : "success"
        : "",
    },
  ];

  const renderFunction = (value, metric) => {
    if (value === undefined || value === null) return null;
    const [answer, index] = getAnswerAndIndex(
      value,
      metric,
      props.data?.wellness_metrics
    );
    const valueColor = getValueColor(
      [],
      { value: answer, textAnswerIndex: index },
      metric.key,
      metric
    );

    return <span style={{ color: valueColor }}>{answer}</span>;
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: {
        compare: (a, b) => new Date(a.date) - new Date(b.date),
      },
    },
    {
      title: "Wellness Score",
      dataIndex: "wellness_score",
      key: "wellness_score",
      sorter: {
        compare: (a, b) => a.wellness_score - b.wellness_score,
      },
      render: (value) => renderFunction(value, wellnessScore),
    },
    ...props.data.wellness_metrics
      .filter(x => x.answer_type !== 'string')
      .filter((metric) => metric.key !== "wellness_score")
      .map((metric) => ({
        title: metric.short,
        dataIndex: metric.key,
        key: metric.key,
        sorter: {
          compare: (a, b) => a[metric.key] - b[metric.key],
        },
        render: (value) => renderFunction(value, metric),
      })),
  ];

  const handleChangeMetric = (index) => (key) => {
    setStatMetrics((prevStatMetrics) => {
      const newStatMetrics = [...prevStatMetrics];
      newStatMetrics[index] = key;
      return newStatMetrics;
    });
  };

  const onChangeStats = () => {
    if (type === "team") {
      dispatch(setTeamFilter({ wellnessStats: !wellnessStats }));
    } else if (type === "player") {
      dispatch(setPlayerFilter({ wellnessStats: !wellnessStats }));
    }
  };

  const handleSaveDefaultMetrics = async () => {
    try {
      await trainingAPI.updateDefaultStats(statMetrics, "subjective");
      dispatch(setWellnessStatDefaults(statMetrics));
      message.success("Saved!");
    } catch (e) {
      console.log(e);
      message.error("Error. Unable to edit ");
    }
  };

  return (
    <>
      <div className="exportable tab-testing testing-stats testing-stats-toggles flex gap-16 mb-16 items-center w-full justify-center">
        {/* <Switch
          checkedChildren="Best"
          unCheckedChildren="Average"
          checked={wellnessStats}
          onChange={onChangeStats}
        /> */}
        <Select
          options={comparison_types}
          value={option}
          onChange={setOption}
          showSearch
          optionFilterProp="value"
          className={`forceplate-stat-comparison`}
          itemx={option}
          style={{ width: 300 }}
        />
        <Button type="primary" size="small" onClick={handleSaveDefaultMetrics}>
          Save Tiles
        </Button>
      </div>
      <div
        className="flex flex-col justify-center items-center w-full"
        id="statistics-widget"
      >
        <Row
          gutter={16}
          style={{ width: "90%", marginBottom: 16 }}
          className="exportable tab-testing testing-stats"
        >
          {statMetrics.map((metric, i) => {
            let {metric_id, metric_stat} = get_metric_info_from_identifier(metric);
            if (metric_stat?.trim() == "") {metric_stat = "avg";}
            return (
              <Col span={6} key={i}>

                {
                  type === "player" ? (
                    <div
                    key={i}
                    style={{
                      width: "100%",
                      height: "100%", 
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: '5px'
                    }}
                  >
                    <CompactStatTile
                      dropdowns={wellnessDropdowns}
                      metric_identifier_with_stat={metric}
                      index={i}
                      onChange={handleChangeMetric(i)}
                      metricType={"wellness"}
                      endDate={endDate}
                      startDate={startDate}
                      stat={metric_stat}
                      option={option}
                      {...(type === "player" && {
                        playerId: player,
                      })}
                      type={type}
                      isCompact={true}
                    />
                  </div>
                  ) : (
                    <CompactStatTile
                      dropdowns={wellnessDropdowns}
                      metric_identifier_with_stat={metric}
                      onChange={handleChangeMetric(i)}
                      option={option}
                      endDate={endDate}
                      startDate={startDate}
                      isCompact={true}
                      type={type}
                      comparison={option}
                      {...(type === "player" && {
                        playerId: player,
                      })}
                      positionFilter={
                        props.position_filter
                      }
                      />
                      
                    // <StatTileV2
                    //   dropdowns={wellnessDropdowns}
                    //   value={getDropdownKey(metric, "metricId")}
                    //   metric_identifier_with_stat={metric}
                    //   index={i}
                    //   onChange={handleChangeMetric(i)}
                    //   metricType={"wellness"}
                    //   endDate={endDate}
                    //   stat={metric_stat}
                    //   option={option}
                    //   {...(type === "player" && {
                    //     playerId: player,
                    //   })}
                    //   type={type}
                    // />
                  )
                }
              </Col>
            );
          })}
        </Row>
      </div>
      <Row justify="center" className="subjective-widget" style={{marginTop: 16}}>
        {boxes.map((box) => (
          <Col span={3} key={box.title} style={{ flexGrow: 1 }}>
            <Statistic
              value={box.sub}
              {...box}
              className={`statistic-tile-${box.className}`}
            />
          </Col>
        ))}
      </Row>

      <Space>
        <div className="resultstable-table">
          <Table
            className="exportable tab-subjective gpsdv-table"
            rowClassName={(row, index) =>
              props.showAll && "ant-table-row-no-shadow"
            }
            columns={columns}
            size="small"
            dataSource={mergedData}
            pagination={
              props.showAll
                ? false
                : {
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30"],
                    position: ["topLeft"],
                  }
            }
            loading={props.loading}
          />
        </div>
      </Space>

      <div className="resultstable-graphs">
        {props.data.length !== 0 ? (
          <SubjectiveCharts
            dataIn={props.data}
            playersPage={props.playersPage}
            player_data={props.player_name || []}
            allGames={
              props.allGames?.filter(
                (gameDay) => gameDay?.opponent?.toLowerCase() !== "tbd"
              ) || []
            }
            {...props.showRestTravelOptions}
          />
        ) : (
          <></>
        )}
      </div>
      <FooterComponent/>
    </>
  );
}